import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ReferralsIconSvg from '../../assets/icons/icon-referral-bonus-referrals.svg?react';
import raffleProfessionalReferralProgressBackgroundImage from '../../assets/images/raffle/raffle-professional-referral-progress-background.png';
import referralBonusTicketImage from '../../assets/images/referral-bonus-ticket.png';

import { rubikTypographyStyles } from './typography.styles';

export type ReferralProgressCardVariant = 'default' | 'professional';

export type ReferralProgressCardProps = {
  chancePercent: number;
  referralsCount: number;
  ticketsCount: number;
  variant?: ReferralProgressCardVariant;
};

type CardProps = {
  $variant: ReferralProgressCardVariant;
};

const Card = styled.section.attrs<CardProps>(({ $variant }) => ({
  'data-ui': 'referral-progress-card',
  'data-variant': $variant,
}))<CardProps>`
  ${rubikTypographyStyles};
  min-height: ${({ theme }) => theme.scale.px(211)};
  border-radius: ${({ theme }) => theme.scale.px(11)};
  background: ${({ $variant }) =>
    $variant === 'professional'
      ? `#eee url(${raffleProfessionalReferralProgressBackgroundImage}) no-repeat center / cover`
      : '#eee'};
  padding: ${({ theme }) => theme.scale.px(20)}
    ${({ theme, $variant }) =>
      $variant === 'professional' ? theme.scale.px(20) : theme.scale.px(13)}
    ${({ theme }) => theme.scale.px(15)};
  box-sizing: border-box;
  display: grid;
  align-content: start;
  overflow: hidden;
`;

const Title = styled.h2<CardProps>`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const ChanceRow = styled.div<CardProps>`
  margin-top: ${({ theme }) => theme.scale.px(6)};
  display: grid;
  row-gap: ${({ theme }) => theme.scale.px(8)};
  align-items: start;
`;

const Multiplier = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(6)};
`;

const TicketIcon = styled.img.attrs({
  src: referralBonusTicketImage,
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(45)};
  height: ${({ theme }) => theme.scale.px(35)};
  display: block;
`;

const MultiplierText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const ChanceDescription = styled.p<CardProps>`
  margin: 0;
  max-width: ${({ theme, $variant }) =>
    $variant === 'professional' ? theme.scale.px(208) : theme.scale.px(250)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const ReferralsRow = styled.div`
  margin-top: ${({ theme }) => theme.scale.px(24)};
  display: grid;
  grid-template-columns: ${({ theme }) =>
    `${theme.scale.px(120)} ${theme.scale.px(20)} auto`};
  column-gap: ${({ theme }) => theme.scale.px(3)};
  align-items: center;
`;

const ReferralsLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const ReferralsValue = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(4)};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const ReferralsIcon = styled(ReferralsIconSvg).attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(15)};
  height: ${({ theme }) => theme.scale.px(15)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const ReferralsSuffix = styled.span`
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const Hint = styled.p<CardProps>`
  margin: 0;
  margin-top: ${({ theme }) => theme.scale.px(13)};
  max-width: ${({ theme, $variant }) =>
    $variant === 'professional' ? theme.scale.px(238) : theme.scale.px(250)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

export function ReferralProgressCard({
  chancePercent,
  referralsCount,
  ticketsCount,
  variant = 'default',
}: ReferralProgressCardProps) {
  const { t } = useTranslation();

  return (
    <Card $variant={variant}>
      <Title $variant={variant}>{t('referralBonus.progressTitle')}</Title>
      <ChanceRow $variant={variant}>
        <Multiplier>
          <TicketIcon />
          <MultiplierText>
            {t('referralBonus.chanceMultiplier', {
              multiplier: ticketsCount,
            })}
          </MultiplierText>
        </Multiplier>
        <ChanceDescription $variant={variant}>
          {t('referralBonus.chanceDescription', {
            percent: chancePercent,
          })}
        </ChanceDescription>
      </ChanceRow>
      <ReferralsRow>
        <ReferralsLabel>{t('referralBonus.referralsLabel')}</ReferralsLabel>
        <ReferralsIcon />
        <ReferralsValue>
          {String(referralsCount)}
          <ReferralsSuffix>
            {t('referralBonus.referralsSuffix')}
          </ReferralsSuffix>
        </ReferralsValue>
      </ReferralsRow>
      <Hint $variant={variant}>{t('referralBonus.progressHint')}</Hint>
    </Card>
  );
}
